import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Button,
  TagPicker,
  TagPickerControl,
  TagPickerInput,
  TagPickerList,
  TagPickerOnOptionSelectData,
  TagPickerOption,
} from "@fluentui/react-components";
import { Dismiss12Regular, BriefcaseRegular } from "@fluentui/react-icons";
import { PersonaItem, PeoplePickerProps } from "../../../types/priorityContacts";
import {
  optionSecondaryTextClass,
  tagPickerWrapperClass,
  selectedListClass,
  selectedItemRowClass,
  selectedItemTextBlockClass,
  selectedItemNameClass,
  selectedItemSecondaryClass,
  selectedItemRemoveBtnClass,
} from "./styles";

export const PriorityContactsSelector: React.FC<PeoplePickerProps> = ({
  people,
  favourites,
  onChange,
  onInputChange,
  defaultSelectedItems,
  disabled,
  placeholder,
  searchTarget,
}) => {
  const [selected, setSelected] = useState<PersonaItem[]>(defaultSelectedItems || []);
  const [query, setQuery] = useState("");
  const userHasInteracted = useRef(false);

  // Populate from defaultSelectedItems once data loads (before user interacts)
  useEffect(() => {
    if (!userHasInteracted.current && defaultSelectedItems && defaultSelectedItems.length > 0) {
      setSelected(defaultSelectedItems);
    }
  }, [defaultSelectedItems]);

  const getItemKey = (item: PersonaItem): string => {
    if (searchTarget === "people") {
      return item.secondaryText || item.address || item.text || "";
    }
    return item.optionalText || item.workspaceId || item.primaryText || item.text || "";
  };

  const getItemLabel = (item: PersonaItem): string => {
    return item.text || item.primaryText || item.displayName || "";
  };

  const selectedValues = selected.map(getItemKey);

  // When query is empty show favourites (minus already-selected); otherwise filter from people list
  const options = query
    ? people.filter((p) => {
        const label = getItemLabel(p).toLowerCase();
        return label.includes(query.toLowerCase()) && !selectedValues.includes(getItemKey(p));
      })
    : favourites.filter((f) => !selectedValues.includes(getItemKey(f)));

  const handleSelect = (_: unknown, data: TagPickerOnOptionSelectData) => {
    userHasInteracted.current = true;
    const pool = [...people, ...selected];
    const newSelected = data.selectedOptions
      .map((val) => pool.find((p) => getItemKey(p) === val))
      .filter((p): p is PersonaItem => Boolean(p));
    const seen = new Set<string>();
    const deduped = newSelected.filter((p) => {
      const k = getItemKey(p);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    setSelected(deduped);
    onChange(deduped);
    setQuery("");
    onInputChange("");
  };

  const handleRemove = (item: PersonaItem) => {
    userHasInteracted.current = true;
    const newSelected = selected.filter((s) => getItemKey(s) !== getItemKey(item));
    setSelected(newSelected);
    onChange(newSelected);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onInputChange(e.target.value);
  };

  return (
    <div className={tagPickerWrapperClass}>
      <TagPicker onOptionSelect={handleSelect} selectedOptions={selectedValues} disabled={disabled}>
        <TagPickerControl>
          <TagPickerInput
            placeholder={placeholder}
            value={query}
            onChange={handleInputChange}
          />
        </TagPickerControl>
        <TagPickerList>
          {options.map((option) => (
            <TagPickerOption
              key={getItemKey(option)}
              value={getItemKey(option)}
              text={getItemLabel(option)}
            >
              <span>{getItemLabel(option)}</span>
              {searchTarget === "people" && option.secondaryText && (
                <span className={optionSecondaryTextClass}>{option.secondaryText}</span>
              )}
            </TagPickerOption>
          ))}
        </TagPickerList>
      </TagPicker>

      {selected.length > 0 && (
        <ul className={selectedListClass}>
          {selected.map((item) => {
            const label = getItemLabel(item);
            const secondary = searchTarget === "people"
              ? (item.secondaryText || item.address || "")
              : "";
            return (
              <li key={getItemKey(item)} className={selectedItemRowClass}>
                {searchTarget === "people" ? (
                  <Avatar name={label} size={28} color="colorful" />
                ) : (
                  <BriefcaseRegular style={{ fontSize: 16 }} />
                )}
                <div className={selectedItemTextBlockClass}>
                  <span className={selectedItemNameClass}>{label}</span>
                  {secondary && (
                    <span className={selectedItemSecondaryClass}>{secondary}</span>
                  )}
                </div>
                <Button
                  appearance="subtle"
                  icon={<Dismiss12Regular />}
                  className={selectedItemRemoveBtnClass}
                  aria-label={`Remove ${label}`}
                  onClick={() => handleRemove(item)}
                  disabled={disabled}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
